import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ListingAnalytics {
  listingId: string;
  views: number;
  inquiries: number;
  recentViews: number; // views in last 7 days
  recentInquiries: number; // inquiries in last 7 days
}

export const useListingAnalytics = (userId: string) => {
  const [analytics, setAnalytics] = useState<ListingAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's listings across all 5 asset types in parallel
      const [heliRes, engineRes, partsRes, jobRes, serviceRes] = await Promise.all([
        supabase.from('helicopter_listings').select('id').eq('user_id', userId),
        supabase.from('engine_listings').select('id').eq('user_id', userId),
        supabase.from('parts_listings').select('id').eq('user_id', userId),
        supabase.from('job_listings').select('id').eq('user_id', userId),
        supabase.from('service_listings').select('id').eq('user_id', userId),
      ]);

      // Check for any errors
      const firstError = [heliRes, engineRes, partsRes, jobRes, serviceRes].find(r => r.error);
      if (firstError?.error) throw firstError.error;

      const listings = [
        ...(heliRes.data || []),
        ...(engineRes.data || []),
        ...(partsRes.data || []),
        ...(jobRes.data || []),
        ...(serviceRes.data || []),
      ];

      if (listings.length === 0) {
        setAnalytics([]);
        return;
      }

      const listingIds = listings.map(l => l.id);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      // Get view counts for all listings
      const { data: viewCounts, error: viewError } = await supabase
        .from('listing_views')
        .select('listing_id')
        .in('listing_id', listingIds);

      if (viewError) throw viewError;

      // Get recent view counts
      const { data: recentViews, error: recentViewError } = await supabase
        .from('listing_views')
        .select('listing_id')
        .in('listing_id', listingIds)
        .gte('viewed_at', sevenDaysAgo.toISOString());

      if (recentViewError) throw recentViewError;

      // Get inquiry counts
      const { data: inquiryCounts, error: inquiryError } = await supabase
        .from('listing_inquiries')
        .select('listing_id')
        .in('listing_id', listingIds);

      if (inquiryError) throw inquiryError;

      // Get recent inquiry counts
      const { data: recentInquiries, error: recentInquiryError } = await supabase
        .from('listing_inquiries')
        .select('listing_id')
        .in('listing_id', listingIds)
        .gte('created_at', sevenDaysAgo.toISOString());

      if (recentInquiryError) throw recentInquiryError;

      // Group and count
      const analyticsMap = new Map<string, ListingAnalytics>();
      
      // Initialize with all listings
      listingIds.forEach(id => {
        analyticsMap.set(id, {
          listingId: id,
          views: 0,
          inquiries: 0,
          recentViews: 0,
          recentInquiries: 0
        });
      });

      // Count views
      viewCounts?.forEach(view => {
        const analytics = analyticsMap.get(view.listing_id);
        if (analytics) {
          analytics.views++;
        }
      });

      // Count recent views
      recentViews?.forEach(view => {
        const analytics = analyticsMap.get(view.listing_id);
        if (analytics) {
          analytics.recentViews++;
        }
      });

      // Count inquiries
      inquiryCounts?.forEach(inquiry => {
        const analytics = analyticsMap.get(inquiry.listing_id);
        if (analytics) {
          analytics.inquiries++;
        }
      });

      // Count recent inquiries
      recentInquiries?.forEach(inquiry => {
        const analytics = analyticsMap.get(inquiry.listing_id);
        if (analytics) {
          analytics.recentInquiries++;
        }
      });

      setAnalytics(Array.from(analyticsMap.values()));
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchAnalytics();
    }
  }, [userId]);

  return { analytics, loading, error, refetch: fetchAnalytics };
};

// Hook for tracking listing views
export const useTrackView = () => {
  const trackView = async (listingId: string) => {
    try {
      // Get IP address and user agent
      const userAgent = navigator.userAgent;
      const referrer = document.referrer;
      
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      // Generate a session ID for grouping views
      const sessionId = sessionStorage.getItem('session_id') || 
        crypto.randomUUID();
      sessionStorage.setItem('session_id', sessionId);

      await supabase
        .from('listing_views')
        .insert({
          listing_id: listingId,
          user_id: user?.id || null,
          user_agent: userAgent,
          referrer: referrer || null,
          session_id: sessionId
        });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  return { trackView };
};

// Hook for creating inquiries
export const useCreateInquiry = () => {
  const [loading, setLoading] = useState(false);

  const createInquiry = async (data: {
    listingId: string;
    message: string;
    contactMethod?: string;
    inquirerEmail?: string;
    inquirerPhone?: string;
    inquirerName?: string;
  }) => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('listing_inquiries')
        .insert({
          listing_id: data.listingId,
          inquirer_id: user?.id || null,
          message: data.message,
          contact_method: data.contactMethod || 'email',
          inquirer_email: data.inquirerEmail,
          inquirer_phone: data.inquirerPhone,
          inquirer_name: data.inquirerName
        });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error creating inquiry:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { createInquiry, loading };
};